import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

/**
 * DesktopSelector (clean + fast jump)
 * Goal:
 * - Replace the always-visible "4a 4b 5a 5b 6a" bar with a clean header:
 *     Exercise 5A • 9/20   +  Prev/Next  +  Jump ▾
 * - Keep quick navigation:
 *   - Prev/Next, First/Last still exist (compact icon buttons)
 *   - Jump dropdown (search + grid + list) is one click away
 * - No duplicated active state: derive from props only.
 * - Flyt og rytme label logic preserved.
 */

// "01a_nodoubles_MP3WRAP.mp3" -> "1a"
function flytOgRytmeLabelFromFilename(filename) {
  if (!filename) return null;
  const base = String(filename).split("/").pop() || "";
  const m = base.match(/^(\d{1,2})([ab])/i);
  if (!m) return null;
  const num = parseInt(m[1], 10);
  const letter = m[2].toLowerCase();
  if (!Number.isFinite(num)) return null;
  return `${num}${letter}`;
}

function defaultLabel(idx) {
  return String(idx + 1);
}

function getTabLabel({ tabsData, idx, courseKey }) {
  if (courseKey === "flyt-og-rytme") {
    const l = flytOgRytmeLabelFromFilename(tabsData?.[idx]);
    if (l) return l.toUpperCase(); // show 5A / 5B
  }
  return defaultLabel(idx);
}

function parseFlytLabel(label) {
  // "12A" -> { n: 12, letter: "A" }
  const m = String(label || "").match(/^(\d{1,2})([AB])$/i);
  if (!m) return null;
  return { n: parseInt(m[1], 10), letter: m[2].toUpperCase() };
}

function buildFlytGroups(tabsData, courseKey) {
  if (courseKey !== "flyt-og-rytme") return null;
  const groups = new Map(); // n -> {A: idx, B: idx}
  for (let i = 0; i < (tabsData?.length ?? 0); i++) {
    const label = getTabLabel({ tabsData, idx: i, courseKey });
    const p = parseFlytLabel(label);
    if (!p) continue;
    if (!groups.has(p.n)) groups.set(p.n, {});
    groups.get(p.n)[p.letter] = i;
  }
  // return sorted array of { n, A?, B? }
  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([n, v]) => ({ n, ...v }));
}

const IconFirst = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
    />
  </svg>
);

const IconPrev = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const IconNext = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const IconLast = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 5l7 7-7 7M5 5l7 7-7 7"
    />
  </svg>
);

const IconChevronDown = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const IconCheck = () => (
  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const NavBtn = ({ title, onClick, disabled, children }) => (
  <button
    type="button"
    disabled={disabled}
    className={[
      "flex items-center justify-center w-8 h-8 rounded-lg border transition-all duration-200 flex-shrink-0",
      disabled
        ? "bg-white/30 text-gray-400 border-white/20 cursor-not-allowed"
        : "bg-white/60 hover:bg-white/80 text-gray-600 border-white/40 hover:shadow-md",
    ].join(" ")}
    onClick={onClick}
    title={title}
  >
    {children}
  </button>
);

const PillBtn = ({ title, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/60 hover:bg-white/80 text-gray-700 border border-white/40 hover:border-white/60 hover:shadow-md transition-all duration-200 text-sm font-medium"
  >
    {children}
  </button>
);

const GridBtn = ({ active, onClick, children, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={[
      "px-2 py-1 rounded-lg border text-xs font-semibold transition-all duration-150",
      disabled
        ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
        : active
        ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-blue-400/30 shadow-sm"
        : "bg-white text-gray-700 border-gray-200 hover:bg-blue-50",
    ].join(" ")}
  >
    {children}
  </button>
);

const DesktopSelector = ({ tabsData, clickHandler, active = 0, courseKey }) => {
  const total = Array.isArray(tabsData) ? tabsData.length : 0;

  // single source of truth
  const activeIdx = total > 0 ? Math.max(0, Math.min(total - 1, active)) : 0;

  const [isOpen, setIsOpen] = useState(false);
  const [q, setQ] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const labelFor = useCallback(
    (idx) => getTabLabel({ tabsData, idx, courseKey }),
    [tabsData, courseKey]
  );

  const handleTabClick = useCallback(
    (idx) => {
      if (!total) return;
      const nextIdx = Math.max(0, Math.min(total - 1, idx));
      clickHandler?.(nextIdx);
    },
    [clickHandler, total]
  );

  // outside click closes
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ESC closes, "/" or "g" opens (optional but handy on desktop)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);

      // light “power user” support; won’t interfere with typing in inputs
      const tag = (e.target?.tagName || "").toLowerCase();
      const inInput = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (inInput) return;

      if ((e.key === "g" || e.key === "G" || e.key === "/") && total > 1) {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [total]);

  // focus search when opened
  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen]);

  const canPrev = activeIdx > 0;
  const canNext = activeIdx < total - 1;

  const title = useMemo(() => {
    const exLabel = labelFor(activeIdx);
    return `Exercise ${exLabel} • ${activeIdx + 1}/${total}`;
  }, [activeIdx, total, labelFor]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return null;

    // match "5a" / "12" / "exercise 5b" / etc.
    const hits = [];
    for (let i = 0; i < total; i++) {
      const label = labelFor(i);
      const hay = `exercise ${label}`.toLowerCase();
      if (hay.includes(query)) hits.push(i);
    }
    return hits.slice(0, 30);
  }, [q, total, labelFor]);

  const flytGroups = useMemo(() => buildFlytGroups(tabsData, courseKey), [tabsData, courseKey]);

  if (!total || total <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-2 relative">
      {/* Clean header bar */}
      <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-xl p-1.5 border border-white/30">
        <NavBtn title="First exercise" disabled={!canPrev} onClick={() => handleTabClick(0)}>
          <IconFirst />
        </NavBtn>

        <NavBtn title="Previous exercise" disabled={!canPrev} onClick={() => handleTabClick(activeIdx - 1)}>
          <IconPrev />
        </NavBtn>

        <div className="px-2 sm:px-3 py-1 rounded-lg bg-white/35 border border-white/30">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-medium text-white/70">Exercise</span>
            <span className="text-sm font-semibold text-white">{labelFor(activeIdx)}</span>
            <span className="text-white/40">•</span>
            <span className="text-sm font-semibold text-white">
              {activeIdx + 1}/{total}
            </span>
          </div>
        </div>

        <NavBtn title="Next exercise" disabled={!canNext} onClick={() => handleTabClick(activeIdx + 1)}>
          <IconNext />
        </NavBtn>

        <NavBtn title="Last exercise" disabled={!canNext} onClick={() => handleTabClick(total - 1)}>
          <IconLast />
        </NavBtn>

        {/* Jump */}
        <div className="relative" ref={dropdownRef}>
          <PillBtn
            title="Jump to exercise (G)"
            onClick={() => setIsOpen((v) => !v)}
          >
            <span className="hidden sm:inline">Jump</span>
            <IconChevronDown />
          </PillBtn>

          {isOpen && (
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[min(92vw,520px)] bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/60 p-4 z-50">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-gray-500">Quick jump</div>
                  <div className="font-semibold text-gray-900">{title}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="w-9 h-9 rounded-xl bg-white/70 border border-white/60 hover:bg-white flex items-center justify-center text-gray-700"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {/* Search */}
              <div className="mt-3">
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Type: 5A, 12B, 9…"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <div className="mt-1 text-[11px] text-gray-500">
                  Tip: press <span className="font-mono">G</span> or <span className="font-mono">/</span> to open.
                </div>
              </div>

              {/* Results (search) */}
              {filtered && (
                <div className="mt-3 max-h-56 overflow-y-auto rounded-xl border border-gray-100 bg-white">
                  {filtered.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500">No matches.</div>
                  ) : (
                    filtered.map((idx) => {
                      const isActive = idx === activeIdx;
                      return (
                        <button
                          key={idx}
                          type="button"
                          className={[
                            "w-full text-left px-4 py-3 text-sm flex items-center justify-between",
                            isActive ? "bg-blue-50 text-blue-800 font-medium" : "hover:bg-gray-50 text-gray-800",
                          ].join(" ")}
                          onClick={() => {
                            handleTabClick(idx);
                            setIsOpen(false);
                            setQ("");
                          }}
                        >
                          <span>Exercise {labelFor(idx)}</span>
                          {isActive && <IconCheck />}
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              {/* Default view (no search): Flyt grid (A/B) or plain list */}
              {!filtered && (
                <div className="mt-4 space-y-3">
                  {flytGroups ? (
                    <>
                      <div className="text-xs font-medium text-gray-500">All exercises</div>
                      <div className="max-h-64 overflow-y-auto rounded-2xl border border-gray-100 bg-white p-3">
                        <div className="grid gap-2">
                          {flytGroups.map((g) => {
                            const aIdx = g.A;
                            const bIdx = g.B;
                            const aActive = aIdx === activeIdx;
                            const bActive = bIdx === activeIdx;
                            return (
                              <div key={g.n} className="flex items-center justify-between gap-3">
                                <div className="text-sm font-semibold text-gray-700 w-10 text-right">
                                  {g.n}
                                </div>
                                <div className="flex items-center gap-2">
                                  <GridBtn
                                    disabled={typeof aIdx !== "number"}
                                    active={aActive}
                                    onClick={() => {
                                      if (typeof aIdx !== "number") return;
                                      handleTabClick(aIdx);
                                      setIsOpen(false);
                                    }}
                                  >
                                    A
                                  </GridBtn>
                                  <GridBtn
                                    disabled={typeof bIdx !== "number"}
                                    active={bActive}
                                    onClick={() => {
                                      if (typeof bIdx !== "number") return;
                                      handleTabClick(bIdx);
                                      setIsOpen(false);
                                    }}
                                  >
                                    B
                                  </GridBtn>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs font-medium text-gray-500">All parts</div>
                      <div className="max-h-64 overflow-y-auto rounded-2xl border border-gray-100 bg-white">
                        {tabsData.map((_, idx) => {
                          const isActive = idx === activeIdx;
                          return (
                            <button
                              key={idx}
                              type="button"
                              className={[
                                "w-full text-left px-4 py-3 text-sm flex items-center justify-between",
                                isActive ? "bg-blue-50 text-blue-800 font-medium" : "hover:bg-gray-50 text-gray-800",
                              ].join(" ")}
                              onClick={() => {
                                handleTabClick(idx);
                                setIsOpen(false);
                              }}
                            >
                              <span>Part {labelFor(idx)}</span>
                              {isActive && <IconCheck />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between pt-1 text-xs text-gray-500">
                    <span>ESC to close</span>
                    <span className="hidden sm:inline">Type to search</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesktopSelector;
